import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Typography, Button, Input, Row, Col, Space, Table, Tag, message, Tooltip, Grid, List } from 'antd';
import type { Key } from 'react';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Payable, PayableStatus, PayableType } from '@/types/finance';
import { getPayables, addPayable, updatePayable, deletePayable } from '@/api/finance';
import { useAuth } from '@/contexts/AuthContext';
import PayableModal from '../../components/features/finance/PayableModal';
import DeleteConfirmationModal from '../../components/common/DeleteConfirmationModal';
import dayjs from 'dayjs';
import { Money } from '@/types';
import { formatFirestoreTimestamp } from '@/utils/date';

const PAGE_SIZE = 10;

const Payables: React.FC = () => {
  const { t } = useTranslation(['payables', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPayable, setEditingPayable] = useState<Payable | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [payableToDelete, setPayableToDelete] = useState<Payable | null>(null);
  
  // Queries
  const { data, isLoading } = useQuery({
    queryKey: ['payables', page, searchTerm],
    queryFn: async () => {
      const lastDoc = page > 1 ? lastDocs[page - 1] : undefined;
      return getPayables({
        searchTerm,
        pageSize: PAGE_SIZE,
        page,
        lastDoc,
        firestoreUser: firestoreUser!,
      });
    },
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    placeholderData: (previousData) => previousData,
    enabled: !!firestoreUser,
  });

  const [lastDocs] = useState<any[]>([]);

  // Mutations
  const addMutation = useMutation({
    mutationFn: (payable: Omit<Payable, 'id' | 'activityRecords' | 'workspace'>) =>
      addPayable(payable, firestoreUser!),
    onSuccess: () => {
      message.success(t('payables:messages.createSuccess'));
      queryClient.invalidateQueries({ queryKey: ['payables'] });
      handleModalCancel();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Payable> }) =>
      updatePayable(id, data, firestoreUser!),
    onSuccess: () => {
      message.success(t('payables:messages.updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['payables'] });
      handleModalCancel();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePayable(id, firestoreUser!),
    onSuccess: () => {
      message.success(t('payables:messages.deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['payables'] });
      setDeleteModalVisible(false);
      setPayableToDelete(null);
    },
    onError: () => {
      message.error(t('payables:messages.deleteError'));
    },
  });

  // Handlers
  const handleAdd = useCallback(() => {
    setEditingPayable(null);
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback((record: Payable) => {
    setEditingPayable(record);
    setIsModalOpen(true);
  }, []);

  const handleView = useCallback((record: Payable) => {
    navigate(`/finance/payables/${record.id}`, { state: { payable: record } });
  }, [navigate]);

  const handleDelete = useCallback((record: Payable) => {
    setPayableToDelete(record);
    setDeleteModalVisible(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (payableToDelete) {
      deleteMutation.mutate(payableToDelete.id);
    }
  }, [payableToDelete, deleteMutation]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteModalVisible(false);
    setPayableToDelete(null);
  }, []);

  const handleModalCancel = useCallback(() => {
    setIsModalOpen(false);
    setEditingPayable(null);
  }, []);

  const handleModalSuccess = useCallback(async (payableData: Omit<Payable, 'id' | 'activityRecords' | 'workspace'>) => {
    if (editingPayable) {
      await updateMutation.mutateAsync({
        id: editingPayable.id,
        data: payableData,
      });
    } else {
      await addMutation.mutateAsync(payableData);
    }
  }, [editingPayable, addMutation, updateMutation]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1);
  }, []);

  // Memoized values
  const filteredData = useMemo(() => {
    return data?.payables || [];
  }, [data]);

  const getStatusTagColor = (status: PayableStatus) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'partial': return 'blue';
      case 'paid': return 'green';
      case 'overdue': return 'red';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getTypeTagColor = (type: PayableType) => {
    switch (type) {
      case 'vendor': return 'blue';
      case 'asset': return 'green';
      case 'employee': return 'purple';
      case 'payroll': return 'orange';
      case 'maintenance': return 'red';
      case 'other': return 'default';
      default: return 'default';
    }
  };

  const formatMoney = (amount: Money) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: amount.currency || 'USD'
    }).format(amount.amount);
  };

  const renderPayableCard = (payable: Payable) => (
    <Card
      key={payable.id}
      hoverable
      style={{ marginBottom: 16 }}
      actions={[
        <Button
          key="view"
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleView(payable)}
        >
          {t('common:actions.view')}
        </Button>,
        <Button
          key="edit"
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEdit(payable)}
        >
          {t('common:actions.edit')}
        </Button>,
        <Button
          key="delete"
          type="link"
          icon={<DeleteOutlined />}
          danger
          onClick={() => handleDelete(payable)}
        >
          {t('common:actions.delete')}
        </Button>,
      ]}
    >
      <Card.Meta
        title={payable.name}
        description={
          <Space direction="vertical" size="small">
            <div>
              <Tag color={getTypeTagColor(payable.type)}>
                {t(`payables:types.${payable.type}`)}
              </Tag>
              <Tag color={getStatusTagColor(payable.status)}>
                {t(`payables:statuses.${payable.status}`)}
              </Tag>
            </div>
            <div>
              <strong>{t('common:amount')}:</strong> {formatMoney(payable.amount)}
            </div>
            <div>
              <strong>{t('common:dueDate')}:</strong> {
                (() => {
                  const formattedDate = formatFirestoreTimestamp(payable.dueDate);
                  return formattedDate ? dayjs(formattedDate).format('MMM DD, YYYY') : 'Invalid date';
                })()
              }
            </div>
          </Space>
        }
      />
    </Card>
  );

  const payableTypes = [
    { text: t('payables:types.vendor'), value: 'vendor' },
    { text: t('payables:types.asset'), value: 'asset' },
    { text: t('payables:types.employee'), value: 'employee' },
    { text: t('payables:types.payroll'), value: 'payroll' },
    { text: t('payables:types.maintenance'), value: 'maintenance' },
    { text: t('payables:types.other'), value: 'other' },
  ];

  const payableStatuses = [
    { text: t('payables:statuses.pending'), value: 'pending' },
    { text: t('payables:statuses.partially_paid'), value: 'partial' },
    { text: t('payables:statuses.paid'), value: 'paid' },
    { text: t('payables:statuses.overdue'), value: 'overdue' },
    { text: t('payables:statuses.cancelled'), value: 'cancelled' },
  ];

  const columns = [
    {
      title: t('common:name'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a: Payable, b: Payable) => a.name.localeCompare(b.name),
      render: (text: string) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: t('common:typeLabel'),
      dataIndex: 'type',
      key: 'type',
      filters: payableTypes,
      onFilter: (value: boolean | Key, record: Payable) => record.type === value,
      render: (type: PayableType) => (
        <Tag color={getTypeTagColor(type)}>
          {t(`payables:types.${type}`)}
        </Tag>
      ),
    },
    {
      title: t('common:amount'),
      dataIndex: 'amount',
      key: 'amount',
      sorter: (a: Payable, b: Payable) => a.amount.amount - b.amount.amount,
      render: (amount: Money) => (
        <span style={{ fontWeight: 500, color: '#1890ff' }}>
          {formatMoney(amount)}
        </span>
      ),
    },
    {
      title: t('common:dueDate'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      sorter: (a: Payable, b: Payable) => {
        const dateA = formatFirestoreTimestamp(a.dueDate);
        const dateB = formatFirestoreTimestamp(b.dueDate);
        if (!dateA || !dateB) return 0;
        return dayjs(dateA).unix() - dayjs(dateB).unix();
      },
      render: (date: any) => {
        const formattedDate = formatFirestoreTimestamp(date);
        if (!formattedDate) return 'Invalid date';
        return dayjs(formattedDate).format('MMM DD, YYYY');
      },
    },
    {
      title: t('common:statusLabel'),
      dataIndex: 'status',
      key: 'status',
      filters: payableStatuses,
      onFilter: (value: boolean | Key, record: Payable) => record.status === value,
      render: (status: PayableStatus) => (
        <Tag color={getStatusTagColor(status)}>
          {t(`payables:statuses.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      render: (_: any, record: Payable) => (
        <Space size="small">
          <Tooltip title={t('common:details')}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title={t('common:actions.edit')}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title={t('common:actions.delete')}>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
              size="small"
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Typography.Title level={3}>{t('payables:title')}</Typography.Title>
        </Col>
        <Col>
          <Space direction={screens.xs ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
            <Input
              placeholder={t('payables:searchPlaceholder')}
              value={searchTerm}
              onChange={handleSearchChange}
              prefix={<SearchOutlined />}
              style={{ width: screens.xs ? '100%' : 300 }}
            />
            <Button type="primary" onClick={handleAdd} icon={<PlusOutlined />}>
              {t('common:actions.add')}
            </Button>
          </Space>
        </Col>
      </Row>
      
      {/* Table */}
      <Card>
        {screens.xs ? (
          <List
            dataSource={filteredData}
            renderItem={renderPayableCard}
            rowKey="id"
            loading={isLoading}
            grid={{ gutter: 16, xs: 1, sm: 1 }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={filteredData}
            loading={isLoading}
            rowKey="id"
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total: data?.total || 0,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                t('common:showingRange', { from: range[0], to: range[1], total }),
              onChange: (newPage) => {
                setPage(newPage);
              }
            }}
            scroll={{ x: 800 }}
          />
        )}
      </Card>
      
      {/* Modal */}
      <PayableModal
        open={isModalOpen}
        payable={editingPayable}
        onSuccess={handleModalSuccess}
        onCancel={handleModalCancel}
        loading={addMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('payables:messages.deleteConfirmTitle', { name: payableToDelete?.name })}
        error={deleteMutation.error as any}
        onErrorDismiss={() => setDeleteModalVisible(false)}
      />
    </div>
  );
};

export default Payables; 